import dayjs from 'dayjs';
import { getEnvironment } from 'utilities/environment';
/* eslint-disable import/no-unassigned-import */
import 'dayjs/locale/en-ca';
import 'dayjs/locale/en-gb';
import 'dayjs/locale/en-ie';
import 'dayjs/locale/en-au';
import 'dayjs/locale/fr-ca';
import 'dayjs/locale/es-us';
import 'dayjs/locale/es';
import 'dayjs/locale/ca';
import 'dayjs/locale/ja';
import 'dayjs/locale/fr';
/* eslint-enable import/no-unassigned-import */

const SHOP_HARDWARE_PREFIX = '/shop/hardware';

// Locale URL segments are always two-letter codes (e.g. /us/en). Anything
// else means the URL doesn't carry a locale at that position (e.g. /login,
// or the /v2 app prefix).
const LOCALE_SEGMENT = /^[a-z]{2}$/i;

const getLocalePath = () => {
  // Strip the optional /v2 app prefix (/shop/hardware/v2/us/en/...) so the
  // locale segments are always at positions 1 and 2.
  return window.location.pathname
    .replace(SHOP_HARDWARE_PREFIX, '')
    .replace(/^\/v2\//, '/')
    .slice(0, 6);
};

// On URLs without a locale path (e.g. /login) fall back to the server's
// environment locale, then to en/us. Previously this produced invalid
// locales like 'undefined-LOGIN' or 'us-V2' that crashed Intl.NumberFormat.
const getLanguageCode = () => {
  const code = getLocalePath().split('/')[2];
  if (LOCALE_SEGMENT.test(code)) {
    return code;
  }
  return getEnvironment()?.lang_code || 'en';
};

const getCountryCode = () => {
  const code = getLocalePath().split('/')[1];
  if (LOCALE_SEGMENT.test(code)) {
    return code;
  }
  return getEnvironment()?.country_code || 'us';
};

const getLocaleString = () => {
  const countryCode = getCountryCode().toUpperCase();
  const langCode = getLanguageCode();

  return langCode === 'ja' ? 'ja' : `${langCode}-${countryCode}`;
};

const localizedShopPath = (version = 'v1') => {
  let versionPath = '';

  if (version?.toLowerCase() === 'v2') {
    versionPath = '/v2';
  }

  return `${SHOP_HARDWARE_PREFIX}${versionPath}${getLocalePath()}`;
};

const DAY_JS_LOCALES = [
  'ca',
  'en-ca',
  'en-gb',
  'en-ie',
  'en-au',
  'en',
  'es-us',
  'es',
  'fr-ca',
  'fr',
  'ja',
];

const localizedDayJs = (
  languageCode = getLanguageCode(),
  countryCode = getCountryCode(),
) => {
  let bestMatch;

  // First attempt to find a matching language + country code
  bestMatch = DAY_JS_LOCALES.find(
    (locale) =>
      locale === `${languageCode.toLowerCase()}-${countryCode.toLowerCase()}`,
  );

  // Then attempt to find a matching language
  bestMatch =
    bestMatch ||
    DAY_JS_LOCALES.find((locale) => locale === languageCode.toLowerCase());

  // If all else fails, default to 'en'
  bestMatch = bestMatch || 'en';

  dayjs.locale(bestMatch);

  return dayjs;
};

export {
  getLanguageCode,
  getCountryCode,
  getLocalePath,
  getLocaleString,
  localizedShopPath,
  localizedDayJs,
};
